// libraries
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
	Button,
	Collapse,
	Form,
	FormInstance,
	Input,
	Modal,
	notification,
	Select,
	Table,
	Tooltip,
	Typography,
} from 'antd';
import { DeleteFilled, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';

// components
import { SharedTable } from '../../../../shared/table';

// services
import * as GroupService from '../../../../../services/bb-api/group';
import * as UserService from '../../../../../services/bb-api/user';
import { txt } from '../../../../../locales/i18n';

function formatDate(date: Date): string {
	const format: string = 'ddd DD/MM-YY HH:mm';
	const dateString: string = moment(date).format(format);
	return dateString;
}

const groupRanks: Record<string, any>[] = [
	{ id: 0, name: txt('group.member.rank hidden') },
	{ id: 1, name: txt('group.member.rank admin') },
	{ id: 2, name: txt('group.member.rank moderator') },
	{ id: 3, name: txt('group.member.rank member') },
	{ id: 4, name: txt('group.member.rank awaiting approval') },
	{ id: 5, name: txt('group.member.rank disapproved') },
	{ id: 6, name: txt('group.member.rank invited') },
	{ id: 7, name: txt('group.member.rank declined invite') },
	{ id: 8, name: txt('group.member.rank declined banned') },
];

export function GroupPageMembers(props: any): JSX.Element {
	const [groupMembers, setGroupMembers] = useState<Record<string, any>[]>([]);
	const [loading, setLoading] = useState<boolean>(true);

	const [showRemoveMemberModal, setShowRemoveMemberModal] = useState<boolean>(false);
	const [removeUserId, setRemoveUserId] = useState<number | null>(null);
	const { id }: Record<string, any> = useParams();

	const getMembers = useCallback(async () => {
		setLoading(true);

		try {
			const groupMembers: Record<string, any>[] = await GroupService.getMembers(id);
			setGroupMembers(groupMembers);
		} catch (error) {
			const message: string = (error as Error).name;
			const description: string = (error as Error).message;
			notification.error({ message, description });
		}

		setLoading(false);
	}, [id]);

	useEffect(() => {
		getMembers();
	}, [getMembers]);

	async function handleRankSelection(userId: number, rankId: number): Promise<void> {
		try {
			await GroupService.updateMemberRank(id, userId, rankId);
			notification.success({ message: txt('notification.update successful') });
		} catch (error) {
			const message: string = (error as Error).name;
			const description: string = (error as Error).message;
			notification.error({ message, description });
		}
	}

	function handleRemoveMember(userId: number): void {
		setRemoveUserId(userId);
		setShowRemoveMemberModal(true);
	}

	async function removeMemberCallback(userId: number | null) {
		if (userId === null) return;

		try {
			await GroupService.removeMember(id, userId);
			setGroupMembers((previous) =>
				previous.filter((member: Record<string, any>) => member.userId !== userId)
			);
			notification.success({ message: txt('notification.deletion successful') });
			setShowRemoveMemberModal(false);
		} catch (error) {
			const message: string = (error as Error).name;
			const description: string = (error as Error).message;
			notification.error({ message, description });
		}
	}

	function renderUser(record: Record<string, any>): JSX.Element {
		return (
			<Typography.Link href={`/users/${record.userId}`} className="user-container">
				{record.username}
				<Typography.Text type="secondary">{record.municipality}</Typography.Text>
			</Typography.Link>
		);
	}

	function renderRank(record: Record<string, any>): JSX.Element {
		return (
			<Select
				bordered={false}
				defaultValue={record.rank}
				dropdownMatchSelectWidth={false}
				onSelect={(rankId: number) => handleRankSelection(record.userId, rankId)}
			>
				{groupRanks.map((groupRank: Record<string, any>) => (
					<Select.Option key={groupRank.id} value={groupRank.id}>
						{groupRank.name}
					</Select.Option>
				))}
			</Select>
		);
	}

	function renderActions(userId: number): JSX.Element {
		return (
			<Button
				type="primary"
				onClick={() => handleRemoveMember(userId)}
				icon={<DeleteFilled />}
				danger
			>
				{txt('global.delete')}
			</Button>
		);
	}

	return (
		<>
			<AddMemberPanel updateMembers={getMembers} />

			<StyledTable rowKey="userId" loading={loading} dataSource={groupMembers}>
				<Table.Column
					title={txt('global.user')}
					width="10rem"
					ellipsis
					render={renderUser}
				/>
				<Table.Column title="Rank" width="5rem" render={renderRank} />
				<Table.Column dataIndex="email" title="Email" width="10rem" ellipsis />
				<Table.Column
					dataIndex="created"
					title={txt('global.created')}
					width="10rem"
					render={formatDate}
				/>
				<Table.Column
					dataIndex="userId"
					title={txt('global.delete')}
					width="6rem"
					render={renderActions}
				/>
			</StyledTable>

			<DeleteMemberModal
				useVisible={[showRemoveMemberModal, setShowRemoveMemberModal]}
				onConfirm={() => removeMemberCallback(removeUserId)}
			/>
		</>
	);
}

function AddMemberPanel(props: Record<string, any>): JSX.Element {
	const [users, setUsers] = useState<any[]>([]);
	const [searchMatches, setSearchMatches] = useState<string>('0');
	const [loading, setLoading] = useState<boolean>(false);
	const [rank, setRank] = useState<number>(3);

	const formRef = useRef<FormInstance>(null);
	const { id }: Record<string, any> = useParams();

	async function handleSearch(values: any): Promise<void> {
		const email: string = values.email;
		const userId: string = values.userId;

		setLoading(true);

		try {
			const { users }: { users: Record<string, any>[]; totalMatches: number } =
				await UserService.query({
					userId,
					amount: 11,
					country: 'all',
					filter1: { search: email, target: 'email' },
				});

			const searchMatches: string = String(users.length);

			setUsers(users);
			setSearchMatches(searchMatches);
			setLoading(false);
		} catch (error) {
			const message: string = (error as Error).name;
			const description: string = (error as Error).message;
			notification.error({ message, description });
		}

		setLoading(false);
	}

	async function handleAddMember(userId: number) {
		try {
			await GroupService.addMember(id, userId, rank);
			notification.success({ message: txt('notification.create successful') });
			await props.updateMembers();

			setUsers([]);
			setSearchMatches('0');

			formRef.current?.setFields([
				{ name: 'email', value: '' },
				{ name: 'userId', value: '' },
			]);
		} catch (error) {
			const message: string = (error as Error).name;
			const description: string = (error as Error).message;
			notification.error({ message, description });
		}
	}

	function renderUser(record: Record<string, any>): JSX.Element {
		return (
			<Typography.Link href={`/users/${record.id}`} className="user-container">
				{record.username}
				<Typography.Text type="secondary">{record.id}</Typography.Text>
			</Typography.Link>
		);
	}

	function renderRank(): JSX.Element {
		return (
			<Select
				bordered={false}
				value={rank}
				dropdownMatchSelectWidth={false}
				onChange={setRank}
			>
				{groupRanks.map((groupRank: Record<string, any>) => (
					<Select.Option key={groupRank.id} value={groupRank.id}>
						{groupRank.name}
					</Select.Option>
				))}
			</Select>
		);
	}

	function renderActions(userId: number): JSX.Element {
		return (
			<Button type="primary" onClick={() => handleAddMember(userId)} icon={<PlusOutlined />}>
				{txt('global.create')}
			</Button>
		);
	}

	return (
		<Collapse collapsible="header" ghost bordered={false} expandIcon={() => <PlusOutlined />}>
			<Collapse.Panel header="Add group member" key="1">
				<Form ref={formRef} layout="inline" onFinish={handleSearch}>
					<Tooltip title="Email">
						<Form.Item name="email">
							<Input placeholder="Email" type="text" allowClear />
						</Form.Item>
					</Tooltip>

					<Tooltip title={txt('global.user id')}>
						<Form.Item name="userId">
							<Input placeholder={txt('global.user id')} type="number" allowClear />
						</Form.Item>
					</Tooltip>

					<Form.Item>
						<Button type="primary" htmlType="submit" loading={loading}>
							{txt('global.search')} <SearchOutlined />
						</Button>
					</Form.Item>

					<Form.Item>
						<Typography.Text type="secondary">
							{txt('global.table query notification', { amount: searchMatches })}
						</Typography.Text>
					</Form.Item>
				</Form>

				{users.length !== 0 && (
					<StyledTable rowKey="id" dataSource={users} loading={loading}>
						<Table.Column
							title={txt('global.user')}
							width="10rem"
							ellipsis
							render={renderUser}
						/>
						<Table.Column
							dataIndex="municipality"
							title={txt('global.municipality')}
							width="8rem"
							ellipsis
						/>
						<Table.Column dataIndex="email" title="Email" width="10rem" ellipsis />
						<Table.Column title="Rank" width="5rem" render={renderRank} />
						<Table.Column
							dataIndex="id"
							title={txt('global.delete')}
							width="6rem"
							render={renderActions}
						/>
					</StyledTable>
				)}
			</Collapse.Panel>
		</Collapse>
	);
}

function DeleteMemberModal(props: any): JSX.Element {
	const [visible, setVisible] = props.useVisible;
	const [loading, setLoading] = useState<boolean>(false);
	const [confirmText, setConfirmText] = useState<string>('');

	async function handleConfirm(): Promise<void> {
		setLoading(true);

		try {
			await props.onConfirm();
		} catch (error) {
			const message: string = (error as Error).name;
			const description: string = (error as Error).message;
			notification.error({ message, description });
		}

		setConfirmText('');
		setLoading(false);
	}

	return (
		<Modal
			okText={[<DeleteFilled key={1} />, <span key={2}>{txt('global.delete')}</span>]}
			cancelText={txt('global.cancel')}
			maskClosable
			visible={visible}
			onCancel={() => setVisible(false)}
			okButtonProps={{
				disabled: confirmText.toLowerCase() !== txt('global.delete').toLowerCase(),
				danger: true,
			}}
			onOk={handleConfirm}
			confirmLoading={loading}
		>
			<p>
				Type the word <b>{txt('global.delete')}</b> in the box below
			</p>
			<Input value={confirmText} onChange={(event) => setConfirmText(event.target.value)} />
		</Modal>
	);
}

const StyledTable = styled(SharedTable)`
	display: flex;
	flex-direction: column;
	gap: 1rem;

	.user-container {
		display: flex;
		gap: 1ch;
	}
`;
